import { FC, MouseEvent, PropsWithChildren, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import usePortal from 'react-useportal';
import { useTheme } from 'styled-components/macro';
import { useClickOutside, useIntersectionObserver } from 'hooks';
import { useEscapeClose, useInert } from './hooks';
import { ScrollableModalProps } from './scrollable-modal.types';
import { VStack } from '../stack/stack.styled';
import {
  Arrow,
  ArrowsWrapper,
  Fade,
  IconsWrapper,
  ModalContainer,
  ModalContentWrapper,
  ModalWrapper,
  NavigationWrapper,
} from './scrollable-modal.styled';
import { Icons } from 'assets';
import { SVG } from '../../svg/svg';

export const ScrollableModal: FC<PropsWithChildren<ScrollableModalProps>> =
  observer(
    ({
      isOpen,
      size = 'l',
      background = 'white',
      minHeight,
      onClose,
      inertSelector,
      contentPadding = '12px',
      children,
    }) => {
      const { Portal } = usePortal();
      const theme = useTheme();
      const modalContainerRef = useRef<HTMLDivElement>(null);
      const contentRef = useRef<HTMLDivElement>(null);

      const [topAnchorRef, isTopIntersection] = useIntersectionObserver({
        initialIsIntersecting: true,
      });
      const [bottomAnchorRef, isBottomIntersection] = useIntersectionObserver({
        initialIsIntersecting: true,
      });

      useInert(isOpen, inertSelector);
      useEscapeClose(onClose, isOpen);
      useClickOutside(contentRef, onClose, [
        '.custom-select-dropdown',
        '.modal-navigations',
        '.media-previewer-wrapper',
        '#default-confirm',
      ]);

      const scrollTo = (
        e: MouseEvent<HTMLDivElement>,
        direction: 'top' | 'bottom'
      ) => {
        e.stopPropagation();

        modalContainerRef.current?.scrollTo({
          top: direction === 'top' ? 0 : modalContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      };

      return isOpen ? (
        <Portal>
          <Fade />
          <ModalWrapper ref={modalContainerRef}>
            <ModalContentWrapper>
              <ModalContainer
                ref={contentRef}
                minHeight={minHeight}
                size={size}
                background={background}
                padding={contentPadding}
                onClick={(e) => e.stopPropagation()}
              >
                <div id="modal-top-anchor" ref={topAnchorRef} />
                <VStack flex={1}>{children}</VStack>
                <div id="modal-bottom-anchor" ref={bottomAnchorRef} />
              </ModalContainer>
              <NavigationWrapper className="modal-navigations">
                <IconsWrapper>
                  <SVG
                    src={Icons.Cross}
                    action={onClose}
                    props={{
                      cursor: 'pointer',
                      color: theme.colors.gray[50],
                      hoverColor: theme.colors.white,
                    }}
                  />
                  <ArrowsWrapper>
                    <Arrow $isShown={!isTopIntersection}>
                      <SVG
                        src={Icons.ArrowLeft}
                        props={{
                          cursor: 'pointer',
                          color: theme.colors.gray[50],
                          hoverColor: theme.colors.white,
                          deg: 90,
                        }}
                        action={(e) => scrollTo(e, 'top')}
                      />
                    </Arrow>
                    <Arrow $isShown={!isBottomIntersection}>
                      <SVG
                        src={Icons.ArrowLeft}
                        props={{
                          cursor: 'pointer',
                          color: theme.colors.gray[50],
                          hoverColor: theme.colors.white,
                          deg: -90,
                        }}
                        action={(e) => scrollTo(e, 'bottom')}
                      />
                    </Arrow>
                  </ArrowsWrapper>
                </IconsWrapper>
              </NavigationWrapper>
            </ModalContentWrapper>
          </ModalWrapper>
        </Portal>
      ) : null;
    }
  );
